<template>
  <div :class="{ 'disabled-area': !$can('company_edit') }">
    <v-form ref="companyForm" lazy-validation>
      <!-- COMPANY MAIN INFORMATION -->
      <v-row no-gutters class="my-5">
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
              v-model="credentials.name"
              :label="$t('common.fields.companyName') + ' *'"
              :placeholder="$t('common.fields.companyNamePlaceholder')"
              :rules="rules.name"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
              v-model="credentials.bin"
              :label="$t('common.fields.bin') + ' *'"
              :placeholder="$t('common.fields.binPlaceholder')"
              :rules="rules.bin"
              v-mask="'############'"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
              v-model="credentials.director"
              :label="$t('common.fields.director') + ' *'"
              :placeholder="$t('common.fields.directorPlaceholder')"
              :rules="rules.director"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
      </v-row>
      <h3 class="weight ma-2">{{ $t('common.labels.contacts') }}</h3>
      <v-row no-gutters class="my-5">
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="credentials.phone"
            :label="$t('common.fields.phone')"
            :placeholder="$t('common.fields.phonePlaceholder')"
            v-mask="'+7##########'"
            type="text"
            class="ma-2"
            autocomplete="off"
          />
        </v-col>
        <v-col
          cols="12"
          md="8"
        >
          <v-text-field
              v-model="credentials.address"
              :label="$t('common.fields.companyAddress') + ' *'"
              :placeholder="$t('common.fields.companyAddressPlaceholder')"
              :rules="rules.address"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
      </v-row>
      <h3 class="weight ma-2">{{ $t('common.labels.bankFields') }}</h3>
      <v-row no-gutters class="my-5">
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
              v-model="credentials.bank"
              :label="$t('common.fields.bank') + ' *'"
              :placeholder="$t('common.fields.bankPlaceholder')"
              :rules="rules.bank"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
          <v-text-field
              v-model="credentials.bankBik"
              :label="$t('common.fields.bankBik') + ' *'"
              :placeholder="$t('common.fields.bankBikPlaceholder')"
              :rules="rules.bankBik"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
              v-model="credentials.account"
              :label="$t('common.fields.account') + ' *'"
              :placeholder="$t('common.fields.accountPlaceholder')"
              :rules="rules.account"
              type="text"
              class="ma-2"
              autocomplete="off"
            />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
          v-if="isEdit && investors.length"
          class="ma-2"
        >
          <v-list
            disabled
            flat
            subheader
            shaped
            dense
          >
            <v-subheader>{{ $t('investorsList.title') }}</v-subheader>
            <v-list-item
              v-for="investor in investors" v-bind:key="investor"
              class="companyItem"
            >
              <v-list-item-content>
                {{ investor }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
        </v-col>
      </v-row>
      <!-- SUBMIT BUTTON -->
      <v-row
        class="d-flex align-end flex-column"
      >
        <v-flex xs12 >
          <!-- Error Alert -->
          <alert-error class="px-3" />
          <!-- Success Alert-->
          <v-layout v-if="successAlert.state" row class="alert-wrapper mt-2 mb-2 px-3">
            <v-flex sm12>
              <v-alert :value="successAlert.state" type="success">
                {{ successAlert.message }}
              </v-alert>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-row v-if="$can('company_edit')">
          <div
            v-if="isEdit"
          >
            <v-btn
              large
              depressed
              :color="getStatusButtonColor()"
              class="mr-2"
              @click="toggleStatusDialog=true"
            >
              {{ getStatusButtonLabel() }}
            </v-btn>
          </div>
          <v-btn
            type="button"
            :disabled="areBtnsDisabled"
            class="warning mr-5"
            depressed
            large
            @click="saveCompany"
          >{{ $t('common.btns.save') }}</v-btn>
        </v-row>
      </v-row>
      <v-overlay :value="isLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-form>
    <toggle-status-dialog
      v-if="isEdit"
      :dialog.sync="toggleStatusDialog"
      :companyId="companyId"
      :status.sync="credentials.status"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { mask } from 'vue-the-mask';
import ToggleStatusDialog from '@/components/company/ToggleStatusDialog.vue';
import AlertError from '@/components/shared/AlertError.vue';

export default {
  name: 'CompanyForm',
  directives: {
    mask,
  },
  components: {
    AlertError,
    ToggleStatusDialog,
  },
  data() {
    return {
      areBtnsDisabled: false,
      isEdit: false,
      toggleStatusDialog: false,
      credentials: {
        name: null,
        bin: null,
        director: null,
        phone: null,
        address: null,
        bank: null,
        bankBik: null,
        account: null,
        status: 1,
      },
      rules: {
        name: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) => /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,100}$/.test(v) || this.$t('common.fields.validation.companyName'),
        ],
        bin: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) => /^[0-9]{12,12}$/.test(v) || this.$t('common.fields.validation.bin'),
        ],
        director: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) => /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ ]{1,100}$/.test(v) || this.$t('common.fields.validation.director'),
        ],
        address: [
          (v) => !!v || this.$t('common.fields.validation.field.required'),
          (v) => /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=.,_-]{1,100}$/.test(v) || this.$t('common.fields.validation.companyAddress'),
        ],
        bank: [
          (v) => {
            if (!v) return true;
            return /^[a-zа-яәғёқңөұүһіA-ZА-ЯӘҒЁҚҢӨҰҮҺІ0-9!/ @#$%^&*)(+=._-]{1,40}$/.test(v) || this.$t('common.fields.validation.bank');
          },
        ],
        bankBik: [
          (v) => {
            if (!v) return true;
            return /^[a-zA-Z0-9]{8,8}$/.test(v) || this.$t('common.fields.validation.bankBik');
          },
        ],
        account: [
          (v) => {
            if (!v) return true;
            return /^[a-hj-npr-zA-HJ-NPR-Z0-9]{1,20}$/.test(v) || this.$t('common.fields.validation.account');
          },
        ],
      },
      investors: [],
    };
  },
  props: {
    companyId: String,
  },
  computed: {
    ...mapGetters({
      successAlert: 'global/getAlertSuccess',
      isLoading: 'global/getLoader',
    }),
  },
  beforeCreate() {
    this.$store.commit('global/setLoader', true);
  },
  async created() {
    if (this.companyId) {
      // get company data
      const { companyId } = this;
      try {
        const response = await this.getCompany(companyId);
        this.isEdit = true;
        if (response.success && response.data) {
          const companyCredentials = this.prepareInputData(response.data);
          this.credentials = companyCredentials;
          this.investors = response.data.investors;
        }
      } catch (error) {
        if (error.status === 404) {
          this.$router.push({ name: 'not-found' });
        }
        this.setLoader(false);
      }
    }
    this.setLoader(false);
  },
  destroyed() {
    this.clearAlertError();
    this.clearAlertSuccess();
  },
  methods: {
    ...mapActions({
      createCompany: 'company/createCompany',
      getCompany: 'company/fetchCompanyData',
      editCompany: 'company/editCompany',
    }),
    ...mapMutations({
      clearAlertError: 'global/clearAlertError',
      clearAlertSuccess: 'global/clearAlertSuccess',
      setAlertSuccess: 'global/setAlertSuccess',
      setLoader: 'global/setLoader',
    }),
    async saveCompany() {
      if (this.$refs.companyForm.validate()) {
        this.areBtnsDisabled = true;
        let response = null;
        try {
          if (this.isEdit) {
            const companyData = {
              companyId: this.companyId,
              data: this.credentials,
            };
            response = await this.editCompany(companyData);
          } else {
            response = await this.createCompany(this.credentials);
          }
          if (response.success) {
            const successAlert = {
              state: true,
              message: this.$t('company.saving.success'),
            };
            if (response.data.message) {
              successAlert.message = response.data.message;
            }
            this.clearAlertError();
            this.setAlertSuccess(successAlert);
            const that = this;
            setTimeout(() => {
              that.clearAlertSuccess();
              that.$router.push({ name: 'companies-list' });
            }, 2500);
          }
        } catch (error) {
          this.areBtnsDisabled = false;
        }
      }
    },
    prepareInputData(data) {
      const outData = data;
      delete outData.id;
      delete outData.createdAt;
      delete outData.updatedAt;

      return outData;
    },
    getStatusButtonColor() {
      if (this.credentials.status === 1) {
        return 'red lighten-1';
      }
      return 'light-green darken-1';
    },
    getStatusButtonLabel() {
      if (this.credentials.status === 1) {
        return this.$t('company.label.deactivationCompany');
      }
      return this.$t('company.label.activationCompany');
    },
  },
};
</script>
<style lang="scss" scoped>
h3 {
  margin-top: 15px;
  font-weight: 300;
}
</style>
